<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import ProductFrame from '../components/ProductFrame.vue'
import { platformFeatures, platformFeaturesEn, productViews, productViewsEn, type ProductView } from '../data/content'

const isEn = useRoute().meta.locale === 'en'
const views = isEn ? productViewsEn : productViews
const features = isEn ? platformFeaturesEn : platformFeatures
const activeViewId = ref<ProductView['id']>('teacher')
const activeView = computed<ProductView>(() => views.find((view) => view.id === activeViewId.value) ?? views[0]!)
</script>

<template>
  <section class="product section-pad page-first" aria-labelledby="product-title">
    <div class="section-heading split-heading">
      <div><p class="section-index">01 / {{ isEn ? 'PRODUCT' : 'PRODUKT' }}</p><h1 id="product-title">{{ isEn ? 'Three perspectives. One course workflow.' : 'Trzy perspektywy. Jeden przebieg zajęć.' }}</h1></div>
      <p>{{ isEn ? 'The interface is not trying to turn everything into a game. It answers three practical questions: what are we doing, where are we, and who needs support?' : 'Interfejs nie próbuje być grą ponad wszystko. Ma dawać jasną odpowiedź: co robimy, gdzie jesteśmy i kto potrzebuje wsparcia.' }}</p>
    </div>
    <div class="product-tabs" role="tablist" :aria-label="isEn ? 'Product views' : 'Widoki produktu'">
      <button v-for="view in views" :id="`tab-${view.id}`" :key="view.id" type="button" role="tab" :aria-selected="activeViewId === view.id" :aria-controls="`panel-${view.id}`" @click="activeViewId = view.id">
        <span>0{{ views.indexOf(view) + 1 }}</span>{{ view.label }}
      </button>
    </div>
    <div :id="`panel-${activeView.id}`" class="product-panel" role="tabpanel" :aria-labelledby="`tab-${activeView.id}`" tabindex="0">
      <div class="product-copy"><p class="eyebrow">{{ activeView.kicker }}</p><h2>{{ activeView.title }}</h2><p>{{ activeView.description }}</p></div>
      <div class="product-media-stack">
        <div class="product-media">
          <p v-if="activeView.imageLabel" class="product-media-label">{{ activeView.imageLabel }}</p>
          <ProductFrame :src="activeView.image" :alt="activeView.alt" :width="activeView.width" :height="activeView.height" />
        </div>
        <div v-if="activeView.secondaryImage && activeView.secondaryAlt" class="product-media">
          <p class="product-media-label">{{ activeView.secondaryImageLabel }}</p>
          <ProductFrame :src="activeView.secondaryImage" :alt="activeView.secondaryAlt" :width="activeView.width" :height="activeView.height" />
        </div>
      </div>
    </div>
  </section>

  <section class="platform-features section-pad" aria-labelledby="features-title">
    <div class="platform-features-heading">
      <div>
        <p class="section-index">02 / {{ isEn ? 'CAPABILITIES' : 'FUNKCJE' }}</p>
        <h2 id="features-title">{{ isEn ? 'The tools needed to run a complete lab course.' : 'Narzędzia potrzebne do prowadzenia całego cyklu laboratoriów.' }}</h2>
      </div>
      <p>{{ isEn ? 'PentestCamp connects course planning, lab delivery and progress analysis. Lecturers work in one process instead of moving data between separate tools.' : 'PentestCamp łączy planowanie zajęć, uruchamianie laboratoriów i analizę postępu. Prowadzący pracuje w jednym procesie, bez przenoszenia danych między osobnymi narzędziami.' }}</p>
    </div>
    <div class="platform-feature-grid">
      <article v-for="feature in features" :key="feature.number">
        <div class="platform-feature-meta"><span>{{ feature.number }}</span><span>{{ feature.category }}</span></div>
        <h3>{{ feature.title }}</h3>
        <p>{{ feature.description }}</p>
      </article>
    </div>
  </section>
</template>
